// KnowledgeBaseNotificationsModal.tsx
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
    tryToFetchKnowledgeBaseNotifications,
    tryToMarkKnowledgeBaseNotificationAsRead,
} from '../../store/sofia/notifications/knowledgeBaseNotificationsSlice';
import ReactDOM from 'react-dom';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

const KnowledgeBaseNotificationsModal: React.FC<Props> = ({
    isOpen,
    onClose,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const {
        notifications,
        notificationsAreLoading,
        unreadCount,
        pageNumber,
        pageSize,
        totalElements,
        totalPages,
    } = useAppSelector((state) => state.knowledgeBaseNotifications);

    // Initial load when modal opens → always load page 1
    useEffect(() => {
        if (isOpen) {
            dispatch(
                tryToFetchKnowledgeBaseNotifications({
                    pageNumber: 1,
                    pageSize: pageSize || 10,
                }),
            );
        }
    }, [dispatch, isOpen, pageSize]);

    if (!isOpen) return null;

    const canLoadMore =
        !notificationsAreLoading &&
        pageNumber < totalPages &&
        notifications.length < totalElements;

    const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
        const target = e.currentTarget;
        const nearBottom =
            target.scrollTop + target.clientHeight >= target.scrollHeight - 16; // small threshold

        if (nearBottom && canLoadMore) {
            dispatch(
                tryToFetchKnowledgeBaseNotifications({
                    pageNumber: pageNumber + 1,
                    pageSize: pageSize || 10,
                }),
            );
        }
    };

    const handleNotificationClick = (n: any) => {
        // If notification has an exception, deep-link to that exception screen
        if (n.status === 'unread') {
            dispatch(tryToMarkKnowledgeBaseNotificationAsRead(n._id));
        }
        if (n.exceptionId) {
            onClose();
            navigate(`/sofia-exceptions?exceptionId=${n.exceptionId}`);
            return;
        }

        // If no exception, but has a report download, we do nothing special on card click
        if (n.reportCsvUrl || n.reportPdfUrl) {
            // user will click the CSV/PDF chips instead
        }
    };

    const BadgeDot = ({ status }: { status: 'read' | 'unread' }) => (
        <span
            className={`inline-flex h-1.5 w-1.5 rounded-full ${
                status === 'unread' ? 'bg-emerald-500' : 'bg-zinc-300'
            }`}
        />
    );

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30">
            <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl">
                {/* Header */}
                <div className="mb-3 flex items-center justify-between">
                    <div>
                        <h2 className="text-sm font-semibold text-zinc-900">
                            Notifications
                        </h2>
                        <p className="mt-0.5 text-[11px] text-zinc-500">
                            Updates from Sofia about your knowledge base.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-full px-2 py-1 text-xs text-zinc-500 hover:bg-zinc-100">
                        Close
                    </button>
                </div>

                <div className="mb-2 flex items-center justify-between text-xs text-zinc-500">
                    <span>
                        Unread:{' '}
                        <span className="font-semibold">{unreadCount}</span>
                    </span>
                    <span>
                        Total:{' '}
                        <span className="font-semibold">
                            {totalElements || notifications.length}
                        </span>
                    </span>
                </div>

                {/* Body */}
                {notificationsAreLoading && notifications.length === 0 ? (
                    <div className="py-6 text-center text-sm text-zinc-500">
                        Loading…
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="py-6 text-center text-sm text-zinc-500">
                        No notifications yet.
                    </div>
                ) : (
                    <ul
                        className="max-h-80 space-y-2 overflow-y-auto"
                        onScroll={handleScroll}>
                        {notifications.map((n) => (
                            <li key={n._id}>
                                {/* whole card clickable */}
                                <button
                                    type="button"
                                    onClick={() => handleNotificationClick(n)}
                                    className={`w-full rounded-2xl border px-3 py-3 text-left transition ${
                                        n.status === 'unread'
                                            ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100'
                                            : 'border-zinc-200 bg-white hover:bg-zinc-50'
                                    }`}>
                                    <div className="flex flex-col gap-2">
                                        {/* top row */}
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <BadgeDot
                                                        status={n.status}
                                                    />
                                                    <div className="truncate text-[11px] font-semibold text-zinc-800">
                                                        {n.title}
                                                    </div>
                                                </div>

                                                <div className="mt-1 text-[11px] text-zinc-600 line-clamp-2">
                                                    {n.message}
                                                </div>

                                                <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-zinc-500">
                                                    {n.targetFileName && (
                                                        <span className="truncate">
                                                            File:{' '}
                                                            {n.targetFileName}
                                                        </span>
                                                    )}
                                                    {n.type && (
                                                        <span className="rounded-full bg-zinc-50 px-2 py-0.5 text-[10px] font-medium text-zinc-600 ring-1 ring-zinc-200">
                                                            {n.type.replaceAll(
                                                                '_',
                                                                ' ',
                                                            )}
                                                        </span>
                                                    )}
                                                    {n.exceptionId && (
                                                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600 ring-1 ring-blue-200">
                                                            Go to exception
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="shrink-0 text-right text-[10px] text-zinc-400">
                                                {n.createdAt
                                                    ? new Date(
                                                          n.createdAt,
                                                      ).toLocaleString()
                                                    : ''}
                                            </div>
                                        </div>

                                        {/* report download buttons */}
                                        {(n.reportCsvUrl || n.reportPdfUrl) && (
                                            <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
                                                {n.reportCsvUrl && (
                                                    <a
                                                        href={n.reportCsvUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        className="inline-flex items-center rounded-full bg-zinc-900 px-3 py-1 text-[11px] font-medium text-white hover:bg-zinc-800">
                                                        Download CSV
                                                    </a>
                                                )}
                                                {n.reportPdfUrl && (
                                                    <a
                                                        href={n.reportPdfUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        className="inline-flex items-center rounded-full bg-white px-3 py-1 text-[11px] font-medium text-zinc-900 ring-1 ring-zinc-300 hover:bg-zinc-50">
                                                        Download PDF
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            </li>
                        ))}

                        {notificationsAreLoading && pageNumber > 1 && (
                            <li className="py-2 text-center text-[11px] text-zinc-500">
                                Loading more…
                            </li>
                        )}
                    </ul>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default KnowledgeBaseNotificationsModal;
